import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { GlobalsVariablesService } from '../../shared/service/global-variables.service';
import { Dashboard, DashboardResumoFinanceiro } from '../dashboard.model';

@Component({
    selector: 'app-dashboard-modern',
    templateUrl: './dashboard-modern.component.html'
})
export class DashboardModernComponent implements OnChanges {

    @Input() filtro: FormGroup;
    @Input() ptBR: any;
    @Input() dashboard: Dashboard;
    @Input() valorVendas: any;
    @Input() valorCompras: any;
    @Input() valorContasAPagar: any;
    @Input() valorContasAReceber: any;
    @Input() duplicatasAVencer: any[] = [];
    @Input() diasRestanteLicenca: any;
    @Input() mensagemCertificado: String;
    @Input() mensagemLicenca: String;
    @Input() version: string;

    @Output() periodoSelecionado = new EventEmitter<void>();

    public resumoFinanceiro: any;
    public opcoesResumoFinanceiro: any;

    constructor(public globalsVariablesService: GlobalsVariablesService) {
        this.opcoesResumoFinanceiro = {
            responsive: true,
            maintainAspectRatio: false,
            legend: {
                display: true,
                position: 'top',
                labels: {
                    usePointStyle: true,
                    boxWidth: 8,
                    fontColor: '#667085',
                    fontSize: 13,
                    padding: 18
                }
            },
            elements: {
                line: {
                    tension: 0.35,
                    borderWidth: 2
                },
                point: {
                    radius: 2,
                    hoverRadius: 4,
                    borderWidth: 1
                }
            },
            tooltips: {
                mode: 'index',
                intersect: false,
                callbacks: {
                    label: (tooltipItem, data) => {
                        const value = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index] || 0;
                        return ' ' + data.datasets[tooltipItem.datasetIndex].label + ': R$ ' + Number(value).toLocaleString('pt-BR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                        });
                    }
                }
            },
            scales: {
                xAxes: [{
                    gridLines: {
                        display: false
                    },
                    ticks: {
                        autoSkip: true,
                        maxTicksLimit: 7,
                        fontColor: '#7b879a',
                        fontSize: 12
                    }
                }],
                yAxes: [{
                    ticks: {
                        beginAtZero: true,
                        fontColor: '#7b879a',
                        fontSize: 12,
                        callback: (value) => 'R$ ' + Number(value).toLocaleString('pt-BR')
                    },
                    gridLines: {
                        color: '#e9eef7',
                        drawBorder: false
                    }
                }]
            }
        };
    }

    public ngOnChanges(changes: SimpleChanges): void {
        this.atualizarResumoFinanceiro();
    }

    // Informa se o backend retornou uma base valida para comparacao percentual.
    public possuiBaseComparacao(percentual: any): boolean {
        return percentual !== null && percentual !== undefined;
    }

    // Retorna a classe visual correspondente ao sentido da variacao.
    public classeVariacao(percentual: any): string {
        if (!this.possuiBaseComparacao(percentual)) {
            return 'neon-financial-comparison--neutral';
        }

        return Number(percentual) >= 0
            ? 'neon-financial-comparison--positive'
            : 'neon-financial-comparison--negative';
    }

    // Retorna o icone de alta ou queda usado nos cards comparativos.
    public iconeVariacao(percentual: any): string {
        return Number(percentual) >= 0 ? 'fa-arrow-up' : 'fa-arrow-down';
    }

    // Exibe a variacao sem sinal, pois a direcao ja e representada pela seta.
    public percentualAbsoluto(percentual: any): number {
        return Math.abs(Number(percentual) || 0);
    }

    // Atualiza o grafico moderno usando a evolucao acumulada retornada pelo backend.
    private atualizarResumoFinanceiro(): void {
        const pontos = this.dashboard && this.dashboard.resumoFinanceiro
            ? this.dashboard.resumoFinanceiro
            : [];

        this.resumoFinanceiro = {
            labels: pontos.map(ponto => this.formatarDataRetorno(ponto.data)),
            datasets: [
                this.criarSerieFinanceira('Vendas', pontos, 'valorVendas', '#2878ff'),
                this.criarSerieFinanceira('A receber', pontos, 'valorDuplicatasReceber', '#27b77a'),
                this.criarSerieFinanceira('A pagar', pontos, 'valorDuplicatasPagar', '#ef5b67'),
                this.criarSerieFinanceira('Compras', pontos, 'valorCompras', '#7b5ce1')
            ]
        };
    }

    // Monta uma serie do grafico a partir da propriedade financeira informada.
    private criarSerieFinanceira(
        label: string,
        pontos: DashboardResumoFinanceiro[],
        propriedade: string,
        color: string): any {
        return {
            label: label,
            data: pontos.map(ponto => this.toNumber(ponto[propriedade])),
            borderColor: color,
            backgroundColor: color,
            pointBackgroundColor: '#ffffff',
            pointBorderColor: color,
            fill: false
        };
    }

    // Formata a data ISO retornada pelo backend no padrao reduzido brasileiro.
    private formatarDataRetorno(data: string): string {
        if (!data || data.indexOf('-') < 0) {
            return data || '';
        }

        const partes = data.split('-');
        return partes[2] + '/' + partes[1];
    }

    // Converte totais opcionais para numeros validos usados pelo grafico.
    private toNumber(value: any): number {
        const parsedValue = Number(value);
        return isNaN(parsedValue) ? 0 : parsedValue;
    }
}
